// Adapted from franky-adl/3d-wave-grid (MIT); see licenses/3d-wave-grid.txt.
export function deformWave(vertexShader) {
  return vertexShader.replace('#include <common>', `
    #include <common>
    uniform sampler2D waveTrail;
    uniform int waveCount;
    uniform float waveTime;
    uniform float waveSpeed;
    uniform float waveWidth;
    uniform float waveFade;
    uniform float waveAttack;
    uniform float waveFrequency;
    uniform float waveAmplitude;
    uniform float waveRigid;
    uniform vec2 cursorPoint;
    uniform float cursorPresence;
    uniform float cursorRadius;
    uniform float cursorDepth;
    attribute float clickPress;
    attribute vec3 flightX;
    attribute vec3 flightY;
    attribute vec3 flightZ;
    attribute vec3 flightOrigin;
    attribute float flightProgress;
    attribute vec4 flightShape;
    varying vec2 flightCornerPoint;
    varying vec4 flightCornerShape;
    varying float flightAmount;
    varying float waveHeight;
  `).replace('#include <begin_vertex>', `
    #include <begin_vertex>
    vec2 origin = instanceMatrix[3].xz;
    float proximity = 1.0 - smoothstep(0.0, cursorRadius, distance(origin, cursorPoint));
    transformed.y -= cursorDepth * cursorPresence * proximity + clickPress;
    waveHeight = 0.0;
    // Curved shapes move as a whole so the wave cannot split their surface.
    if (waveRigid > 0.5 || position.y > 0.0) {
      float total = 0.0;
      for (int i = 0; i < waveCount; i++) {
        vec4 point = texture2D(waveTrail, vec2((float(i) + 0.5) / 128.0, 0.5));
        float age = max(0.0, waveTime - point.z);
        float distanceToPoint = distance(origin, point.xy);
        float front = distanceToPoint - age * waveSpeed;
        float weight = exp(-front * front / (waveWidth * waveWidth) - age / waveFade)
          * point.w / (1.0 + distanceToPoint * 0.1);
        // Ease new samples in to avoid a jump when the pointer starts moving.
        weight *= smoothstep(0.0, waveAttack, age);
        waveHeight += weight * cos(front * waveFrequency);
        total += weight;
      }
      waveHeight /= max(total, 1.0);
      transformed.y += clamp(waveHeight, -1.0, 1.0) * waveAmplitude;
    }
    flightAmount = flightProgress;
    flightCornerShape = flightShape;
    flightCornerPoint = position.xz * vec2(length(flightX), length(flightZ));
    if (flightProgress > 0.0) {
      float travel = min(flightProgress / 0.8, 1.0);
      travel = 0.2 * travel + 0.8 * sin(travel * 1.57079632679);
      transformed = mix(transformed, position, travel);
      transformed = mat3(flightX, flightY, flightZ) * transformed + flightOrigin;
    }
  `).replace('#include <beginnormal_vertex>', `
    #include <beginnormal_vertex>
    if (flightProgress > 0.0) {
      objectNormal = mat3(normalize(flightX), normalize(flightY), normalize(flightZ)) * objectNormal;
    }
  `);
}

// The same animated silhouette is used by the visible surface, shadows and picking.
export function roundFlight(fragmentShader, antialias = false) {
  return `varying vec2 flightCornerPoint;\nvarying vec4 flightCornerShape;\n${fragmentShader}`
    .replace(/void main\(\)\s*\{/, `void main() {
      float flightCoverage = 1.0;
      float radius = flightCornerShape.z;
      if (radius > 0.00001) {
        vec2 corner = max(abs(flightCornerPoint) - (flightCornerShape.xy - radius), 0.0) / radius;
        // Straight edges belong to the mesh silhouette, not the corner cutout.
        float edge = pow(corner.x, flightCornerShape.w) + pow(corner.y, flightCornerShape.w) - 1.0;
        ${antialias ? `float width = max(fwidth(edge), 0.00001);
        if (min(corner.x, corner.y) > 0.0) flightCoverage = 1.0 - smoothstep(-width * 0.5, width * 0.5, edge);
        if (flightCoverage <= 0.0) discard;` : `if (min(corner.x, corner.y) > 0.0 && edge > 0.0) discard;`}
      }
    `)
    .replace('#include <opaque_fragment>', '#include <opaque_fragment>' + (antialias ? '\ngl_FragColor.a = flightCoverage;' : ''));
}

export const lensShader = {
  uniforms: {
    tDiffuse: { value: null },
    bentoRects: { value: [] },
    bentoPresence: { value: new Float32Array(10) },
    resolution: { value: null },
    aberration: { value: 0.0035 },
    blurBase: { value: 0.4 },
    blurEdge: { value: 2.5 },
    vignette: { value: 0.07 },
    edgeStart: { value: 0.22 },
    edgeSoftness: { value: 0.46 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform vec4 bentoRects[10];
    uniform float bentoPresence[10];
    uniform vec2 resolution;
    uniform float aberration;
    uniform float blurBase;
    uniform float blurEdge;
    uniform float vignette;
    uniform float edgeStart;
    uniform float edgeSoftness;
    varying vec2 vUv;

    vec3 softSample(vec2 uv, float radius) {
      vec2 stepUV = vec2(radius) / resolution;
      return texture2D(tDiffuse, uv).rgb * 0.4
        + texture2D(tDiffuse, uv + vec2(stepUV.x, 0.0)).rgb * 0.15
        + texture2D(tDiffuse, uv - vec2(stepUV.x, 0.0)).rgb * 0.15
        + texture2D(tDiffuse, uv + vec2(0.0, stepUV.y)).rgb * 0.15
        + texture2D(tDiffuse, uv - vec2(0.0, stepUV.y)).rgb * 0.15;
    }

    void main() {
      vec2 direction = vUv - 0.5;
      float edge = smoothstep(edgeStart, edgeStart + edgeSoftness, length(direction));
      float lensStrength = 1.0;
      for (int i = 0; i < 10; i++) {
        vec2 outside = max(bentoRects[i].xy - vUv, vUv - bentoRects[i].zw) * resolution;
        float protection = 1.0 - smoothstep(12.0, 28.0, max(outside.x, outside.y));
        lensStrength *= 1.0 - protection * bentoPresence[i];
      }
      vec2 shift = direction * aberration * edge * 2.0 * lensStrength;
      float blur = (blurBase + edge * blurEdge) * lensStrength;
      vec3 result = vec3(
        softSample(vUv + shift, blur).r,
        softSample(vUv, blur).g,
        softSample(vUv - shift, blur).b
      );
      gl_FragColor = vec4(result * (1.0 - edge * vignette * lensStrength), 1.0);
    }
  `,
};
